import { isValidEmail } from "../../../utils/index";

export const PEOPLE_FIELD_LIMITS = {
  name: 32,
  email: 64,
  phone: 20,
  company: 32,
  jobTitle: 100,
  teamName: 32,
  teamDescription: 500,
  groupName: 32,
};

export const peopleTableEllipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  width: "100%",
  maxWidth: "100%",
};

/** Single-line ellipsis for sidebar headers, fields, and list rows. */
export const peopleEllipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  maxWidth: "100%",
};

/** Single-line ellipsis for compact single-line truncation. */
export const peopleDescriptionSingleLineSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  maxWidth: "100%",
  width: "100%",
  display: "block",
};

/** Header description: half page width, wraps to show all text. */
export const peopleHeaderDescriptionSx = {
  maxWidth: "50%",
  width: "100%",
  minWidth: 0,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
};

/** Full description body that wraps and grows with content. */
export const peopleDescriptionBodySx = {
  minWidth: 0,
  width: "100%",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
};

/** @deprecated Use peopleHeaderDescriptionSx or peopleDescriptionBodySx */
export const peopleDescriptionClampSx = peopleHeaderDescriptionSx;

/** Two-line clamp for narrow sidebars. */
export const peopleSidebarClamp2Sx = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  minWidth: 0,
  maxWidth: "100%",
};

export function peopleFieldMaxLengthProps(field) {
  const max = PEOPLE_FIELD_LIMITS[field];
  return max ? { inputProps: { maxLength: max } } : {};
}

function maxLengthError(label, max) {
  return `${label} must be ${max} characters or less`;
}

export function validateMaxLength(value, max, label) {
  if (!value || value.length <= max) return "";
  return maxLengthError(label, max);
}

export const peopleFormErrorTextSx = {
  color: "#FF4D4F",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  mt: 0.5,
};

export function fieldErrorSx(hasError) {
  if (!hasError) return {};
  return {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#FF4D4F !important" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FF4D4F !important" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF4D4F !important" },
  };
}

export function hasValidationErrors(errors) {
  return Object.values(errors).some(Boolean);
}

export function firstValidationError(errors) {
  return Object.values(errors).find(Boolean) || null;
}

export function validateCustomerForm(form) {
  const errors = { name: "", email: "", phone: "", company: "" };
  const name = (form.name || "").trim();
  const email = (form.email || "").trim();
  const phone = (form.phone || "").trim();
  const company = (form.company || "").trim();

  if (!name) errors.name = "Name is required";
  else if (name.length < 2) errors.name = "Name must be at least 2 characters";
  else errors.name = validateMaxLength(name, PEOPLE_FIELD_LIMITS.name, "Name");

  if (!email) errors.email = "Email is required";
  else if (!isValidEmail(email)) errors.email = "Enter a valid email address";
  else errors.email = validateMaxLength(email, PEOPLE_FIELD_LIMITS.email, "Email");

  if (phone && phone.replace(/\D/g, "").length < 5) {
    errors.phone = "Enter a valid phone number";
  } else if (phone) {
    errors.phone = validateMaxLength(phone, PEOPLE_FIELD_LIMITS.phone, "Phone");
  }

  if (company) {
    errors.company = validateMaxLength(company, PEOPLE_FIELD_LIMITS.company, "Company");
  }

  return errors;
}

export function validateAgentForm(form) {
  const errors = { name: "", email: "", roleName: "", jobTitle: "" };
  const name = (form.name || "").trim();
  const email = (form.email || "").trim();
  const jobTitle = (form.jobTitle || "").trim();

  if (!name) errors.name = "Name is required";
  else if (name.length < 2) errors.name = "Name must be at least 2 characters";
  else errors.name = validateMaxLength(name, PEOPLE_FIELD_LIMITS.name, "Name");

  if (!email) errors.email = "Email is required";
  else if (!isValidEmail(email)) errors.email = "Enter a valid email address";
  else errors.email = validateMaxLength(email, PEOPLE_FIELD_LIMITS.email, "Email");

  if (jobTitle) {
    errors.jobTitle = validateMaxLength(jobTitle, PEOPLE_FIELD_LIMITS.jobTitle, "Job title");
  }

  if (!form.roleName) errors.roleName = "Role is required";

  return errors;
}

export function validateGroupForm(form, { requireStatus = false } = {}) {
  const errors = { name: "", status: "" };
  const name = (form.name || "").trim();

  if (!name) errors.name = "Group name is required";
  else if (name.length < 2) errors.name = "Group name must be at least 2 characters";
  else errors.name = validateMaxLength(name, PEOPLE_FIELD_LIMITS.groupName, "Group name");

  if (requireStatus && !form.status) {
    errors.status = "Status is required";
  }

  return errors;
}

export function validateTeamForm(form, { requireGroup = true } = {}) {
  const errors = { name: "", description: "", groupId: "" };
  const name = (form.name || "").trim();
  const description = (form.description || "").trim();
  const hasGroup = Boolean(
    form.groupId || (Array.isArray(form.groupIds) && form.groupIds.length > 0)
  );

  if (!name) errors.name = "Team name is required";
  else if (name.length < 2) errors.name = "Team name must be at least 2 characters";
  else errors.name = validateMaxLength(name, PEOPLE_FIELD_LIMITS.teamName, "Team name");

  if (!description) errors.description = "Description is required";
  else if (description.length < 3) {
    errors.description = "Description must be at least 3 characters";
  } else {
    errors.description = validateMaxLength(
      description,
      PEOPLE_FIELD_LIMITS.teamDescription,
      "Description"
    );
  }

  if (requireGroup && !hasGroup) errors.groupId = "Group is required";

  return errors;
}
